import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

// material import
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Header2 from "../../ui-components/headerii";

// service import
import adminDBServices from "../../services/v1/adminDBServices";
import DropdownServices from "../../services/v1/dropdownServices";

// Import your custom Snackbar hook
import { useSnackbar } from "../../ui-components/snackbarProvider";

const OrderEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [editableReservationData, setEditableReservationData] = useState(null);
  const [originalReservedDate, setOriginalReservedDate] = useState(null); // State for the original date
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Title/Country dropdown options, same source as the public checkout form
  const [titleOptions, setTitleOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [originalPackageId, setOriginalPackageId] = useState(null);

  // Recurring donors have every future occurrence pre-generated up front
  // (a Sponsor Lifetime donor has 100 rows) -- when there's more than
  // one, the admin gets asked whether a date change should move just
  // this occurrence or all of them.
  const [isRecurring, setIsRecurring] = useState(false);
  const [futureReservations, setFutureReservations] = useState([]);
  const [dateChangeScope, setDateChangeScope] = useState("single");

  // New states for date availability check
  const [bookedDates, setBookedDates] = useState([]);
  const [dateAvailabilityMessage, setDateAvailabilityMessage] = useState("");
  const [isFetchingBookedDates, setIsFetchingBookedDates] = useState(false);
  const [isDateAvailable, setIsDateAvailable] = useState(null); // null, true, or false

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = dayjs(dateString);
    return date.isValid() ? date.format("YYYY-MM-DD") : "";
  };

  const extractUrlData = () => {
    const queryParams = new URLSearchParams(location.search);
    const encodedData = queryParams.get("data");

    if (encodedData) {
      try {
        let base64 = encodedData.replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4) {
          base64 += "=";
        }
        const jsonString = atob(base64);
        return JSON.parse(jsonString);
      } catch (error) {
        console.error("Failed to decode URL data:", error);
        setError("Invalid reservation data in URL. (Decoding error)");
        return null;
      }
    }
    return null;
  };

  // --- START OF checkDateAvailability FUNCTION ---
  const checkDateAvailability = useCallback(
    (selectedDate) => {
      if (!bookedDates || !originalReservedDate) {
        setDateAvailabilityMessage("Checking date availability...");
        setIsDateAvailable(null); // Keep it in an indeterminate state
        return;
      }

      // 1. If the selected date IS the original date of THIS reservation, it's always available.
      if (selectedDate === originalReservedDate) {
        setDateAvailabilityMessage("This is the current reserved date.");
        setIsDateAvailable(true);
        return;
      }

      // 2. Otherwise, check it's not booked by another reservation.
      const datesBookedByOthers = bookedDates.filter(
        (date) => date !== originalReservedDate
      );

      if (datesBookedByOthers.includes(selectedDate)) {
        setDateAvailabilityMessage(
          "This date is already booked by another reservation."
        );
        setIsDateAvailable(false);
      } else {
        setDateAvailabilityMessage("This date is available.");
        setIsDateAvailable(true);
      }
    },
    [originalReservedDate, bookedDates]
  );
  // --- END OF checkDateAvailability FUNCTION ---

  // --- START OF useEffect BLOCK ---
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

      const urlData = extractUrlData();

      let formattedInitialDate = "";

      if (!urlData || !urlData.reservationID) {
        setLoading(false);
        setError("No valid reservation ID provided for editing.");
        return;
      }

      try {
        // Dropdown options for Title/Country, same public endpoints the
        // checkout form uses.
        const [titles, countries, packages] = await Promise.all([
          DropdownServices.allTitles(),
          DropdownServices.allCountries(),
          adminDBServices.getAllPackages(),
        ]);
        setTitleOptions(titles.data || []);
        setCountryOptions(countries.data || []);
        setPackageOptions(packages?.data?.data || []);

        // Always load the donor's CURRENT full details from the backend
        // (not just whatever was encoded in the URL when the admin clicked
        // Edit) so title/city/country/message are accurate and editable
        // even for records that were missing them.
        let donorDetails = {};
        if (urlData.donorID) {
          const donorResponse = await adminDBServices.getDonorDetails(
            urlData.donorID
          );
          donorDetails = donorResponse?.data?.data?.donor_details || {};
        }

        // Recurrence info + every future occurrence for this donor, so we
        // can offer "just this date" vs "all future dates" and find the
        // reservation's current package id (the URL payload only carries
        // the package title, not its id).
        let recurrenceInfo = { is_recurring: false, reservations: [] };
        if (urlData.donorID) {
          const reservationsResponse = await adminDBServices.getDonorReservations(
            urlData.donorID
          );
          recurrenceInfo = reservationsResponse?.data?.data || recurrenceInfo;
        }
        setIsRecurring(Boolean(recurrenceInfo.is_recurring));
        setFutureReservations(recurrenceInfo.reservations || []);

        const currentReservation = (recurrenceInfo.reservations || []).find(
          (r) => String(r.reservation_id) === String(urlData.reservationID)
        );
        const currentPackageId = currentReservation?.package_id ?? null;
        setOriginalPackageId(currentPackageId);

        const fetchedReservationData = {
          reservationID: urlData.reservationID,
          donorID: urlData.donorID,
          reservation_number: urlData.reservation_number || "N/A",
          fullname: donorDetails.fullname ?? urlData.fullname ?? "",
          contactNo: donorDetails.contactNo ?? urlData.contactNo ?? "",
          whatsappNo: donorDetails.whatsappNo ?? urlData.whatsappNo ?? "",
          email: donorDetails.email ?? urlData.email ?? "",
          profTitle: donorDetails.profTitle ?? "",
          country: donorDetails.country ?? "",
          city: donorDetails.city ?? "",
          message: donorDetails.reasonForSponsoring ?? "",
          package_title: currentReservation?.package_title || urlData.package_title || "",
          packageId: currentPackageId,
          reservedDate: urlData.reservedDate || dayjs().format("YYYY-MM-DD"),
          package_price: urlData.package_price || 0,
          payment_type: urlData.payment_type || "",
          payment_option: urlData.payment_option || "",
        };

        formattedInitialDate = formatDate(fetchedReservationData.reservedDate);
        setOriginalReservedDate(formattedInitialDate);

        // Fetch all reserved dates for availability checking
        setIsFetchingBookedDates(true);
        const response = await adminDBServices.getReservedDates();

        const dates = Array.isArray(response?.data?.data)
          ? response.data.data.map((item) =>
              dayjs(item.date).format("YYYY-MM-DD")
            )
          : [];
        setBookedDates(dates);

        setEditableReservationData({
          ...fetchedReservationData,
          reservedDate: formattedInitialDate,
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(
          "Failed to load reservation details or booked dates. Please try again."
        );
        showSnackbar(
          "Failed to load data: " + (err.message || "Server error"),
          { severity: "error" }
        );
      } finally {
        setLoading(false);
        setIsFetchingBookedDates(false);
        checkDateAvailability(formattedInitialDate);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, showSnackbar]);

  // --- END OF useEffect BLOCK ---

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditableReservationData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "reservedDate") {
      checkDateAvailability(value);
    }
  };

  const handleUpdateReservation = async () => {
    setIsUpdating(true);
    setError(null);

    if (!editableReservationData.reservedDate) {
      showSnackbar("Please select a reservation date.", {
        severity: "warning",
      });
      setIsUpdating(false);
      return;
    }

    if (
      editableReservationData.reservedDate !== originalReservedDate &&
      isDateAvailable === false
    ) {
      showSnackbar(
        "The selected date is already booked. Please choose an available date.",
        { severity: "warning" }
      );
      setIsUpdating(false);
      return;
    }

    try {
      // Contact/personal-info fields always go through update-donor-details.
      // profTitle/country back an Autocomplete whose "nothing selected"
      // state is the empty string from our initial-load default — the
      // backend's append_field() only skips a field when it's exactly
      // None/null, so an empty string for these gets sent through and
      // fails inserting into an integer column (prof_titleID/countryID).
      // Coerce "" to null here so an untouched dropdown is a true no-op.
      const donorInfoUpdate = {
        fullname: editableReservationData.fullname,
        contactNo: editableReservationData.contactNo,
        whatsappNo: editableReservationData.whatsappNo,
        email: editableReservationData.email,
        profTitle: editableReservationData.profTitle || null,
        country: editableReservationData.country || null,
        city: editableReservationData.city,
        reasonForSponsoring: editableReservationData.message,
      };

      const donorUpdateResponse = await adminDBServices.updateDonorDetails(
        editableReservationData.donorID,
        donorInfoUpdate
      );

      if (donorUpdateResponse.status !== 200) {
        throw new Error(
          donorUpdateResponse.data?.message || "Failed to update donor details."
        );
      }

      // Package only needs its own call if it actually changed.
      if (
        editableReservationData.packageId &&
        String(editableReservationData.packageId) !== String(originalPackageId)
      ) {
        const packageUpdateResponse = await adminDBServices.updateReservationPackage(
          editableReservationData.reservationID,
          editableReservationData.packageId
        );
        if (packageUpdateResponse.status !== 200) {
          throw new Error(
            packageUpdateResponse.data?.message || "Failed to update package."
          );
        }
        setOriginalPackageId(editableReservationData.packageId);
      }

      // The date only needs its own call if it actually changed.
      if (editableReservationData.reservedDate !== originalReservedDate) {
        if (isRecurring && futureReservations.length > 1 && dateChangeScope === "series") {
          // Shift every remaining occurrence by the same offset, rather
          // than moving only the one row this page was opened for.
          const offsetDays = dayjs(editableReservationData.reservedDate).diff(
            dayjs(originalReservedDate),
            "day"
          );
          const updates = futureReservations.map((r) => ({
            reservationID: r.reservation_id,
            donorID: editableReservationData.donorID,
            oldReservedDate: r.date,
            reservedDate: dayjs(r.date).add(offsetDays, "day").format("YYYY-MM-DD"),
          }));

          const batchResponse = await adminDBServices.updateReservedDatesBatch(updates);
          if (batchResponse.status !== 200) {
            throw new Error(
              batchResponse.data?.message || "Failed to update reservation dates."
            );
          }
        } else {
          const dataToUpdate = {
            reservationID: editableReservationData.reservationID,
            donorID: editableReservationData.donorID,
            fullname: editableReservationData.fullname,
            contactNo: editableReservationData.contactNo,
            whatsappNo: editableReservationData.whatsappNo,
            email: editableReservationData.email,
            package_title: editableReservationData.package_title,
            reservedDate: editableReservationData.reservedDate,
            oldReservedDate: originalReservedDate,
            package_price: editableReservationData.package_price,
            payment_type: editableReservationData.payment_type,
            payment_option: editableReservationData.payment_option,
          };

          const dateUpdateResponse = await adminDBServices.updateReservedDate(
            dataToUpdate
          );

          if (dateUpdateResponse.status !== 200) {
            throw new Error(
              dateUpdateResponse.data?.message || "Failed to update reservation date."
            );
          }
        }

        setOriginalReservedDate(editableReservationData.reservedDate);
      }

      showSnackbar("Reservation updated successfully!", {
        severity: "success",
      });
    } catch (err) {
      console.error("Update Error:", err);
      setError(
        err.message || "An error occurred while updating the reservation."
      );
      showSnackbar(
        "Failed to update reservation: " + (err.message || "Server error"),
        { severity: "error" }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress color="secondary" />
        <Typography sx={{ ml: 2 }}>Loading reservation details...</Typography>
      </Box>
    );
  }

  if (error && !editableReservationData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          flexDirection: "column",
        }}
      >
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/admin/donations")}
          sx={{ mt: 2 }}
        >
          Go to Donations
        </Button>
      </Box>
    );
  }

  if (!editableReservationData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography variant="h6" color="textSecondary">
          No reservation data to display.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header2 />
      <Typography
        variant="h5"
        sx={{
          mt: 3,
          fontWeight: "semibold",
          fontVariantCaps: "all-petite-caps",
          textAlign: "center",
        }}
      >
        Edit Reservation Details
      </Typography>
      <Grid container justifyContent="center" sx={{ mt: 3 }}>
        <Grid item xs={10} sm={8} md={6}>
          <Box
            sx={{
              p: 3,
              border: "1px solid lightgrey",
              borderRadius: "8px",
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Reservation Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reservation No"
                  name="reservation_number"
                  value={editableReservationData.reservation_number}
                  fullWidth
                  margin="normal"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={titleOptions}
                  getOptionLabel={(option) => option.title || ""}
                  value={
                    titleOptions.find(
                      (option) => option.title === editableReservationData.profTitle
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    setEditableReservationData((prev) => ({
                      ...prev,
                      profTitle: newValue ? newValue.title : "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Title" margin="normal" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Donor Name"
                  name="fullname"
                  value={editableReservationData.fullname}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact No"
                  name="contactNo"
                  value={editableReservationData.contactNo}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="WhatsApp No"
                  name="whatsappNo"
                  value={editableReservationData.whatsappNo}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={editableReservationData.email}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  name="city"
                  value={editableReservationData.city}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countryOptions}
                  getOptionLabel={(option) => option.country || ""}
                  value={
                    countryOptions.find(
                      (option) => option.country === editableReservationData.country
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    setEditableReservationData((prev) => ({
                      ...prev,
                      country: newValue ? newValue.country : "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Country" margin="normal" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Message (Reason for Sponsoring)"
                  name="message"
                  value={editableReservationData.message}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Package"
                  name="packageId"
                  value={editableReservationData.packageId ?? ""}
                  onChange={(e) => {
                    const selected = packageOptions.find(
                      (p) => String(p.packageID) === String(e.target.value)
                    );
                    setEditableReservationData((prev) => ({
                      ...prev,
                      packageId: selected?.packageID ?? null,
                      package_title: selected?.title ?? prev.package_title,
                    }));
                  }}
                  fullWidth
                  margin="normal"
                  select
                >
                  {packageOptions.map((pkg) => (
                    <MenuItem key={pkg.packageID} value={pkg.packageID}>
                      {pkg.title}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reserved Date"
                  name="reservedDate"
                  type="date"
                  value={editableReservationData.reservedDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                  error={isDateAvailable === false}
                  helperText={
                    isFetchingBookedDates ? (
                      <Box display="flex" alignItems="center">
                        <CircularProgress size={16} sx={{ mr: 1 }} /> Checking
                        availability...
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            isDateAvailable === true
                              ? "green"
                              : isDateAvailable === false
                              ? "red"
                              : "text.secondary",
                        }}
                      >
                        {dateAvailabilityMessage}
                      </Typography>
                    )
                  }
                  disabled={isFetchingBookedDates}
                />
              </Grid>
            </Grid>

            {isRecurring && futureReservations.length > 1 && (
              <Box sx={{ mt: 1, mb: 2, p: 2, bgcolor: "#f7f2f5", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  This is a recurring sponsorship with {futureReservations.length}{" "}
                  upcoming dates. If you change the reserved date above, should it
                  apply to:
                </Typography>
                <RadioGroup
                  value={dateChangeScope}
                  onChange={(e) => setDateChangeScope(e.target.value)}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio size="small" />}
                    label="Just this date"
                  />
                  <FormControlLabel
                    value="series"
                    control={<Radio size="small" />}
                    label={`All ${futureReservations.length} future dates (shifted by the same amount)`}
                  />
                </RadioGroup>
              </Box>
            )}

            {error && (
              <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
                {error}
              </Typography>
            )}
            {successMessage && (
              <Typography
                color="success.main"
                sx={{ mt: 2, textAlign: "center" }}
              >
                {successMessage}
              </Typography>
            )}

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleUpdateReservation}
                disabled={
                  isUpdating ||
                  isFetchingBookedDates ||
                  isDateAvailable === false
                }
              >
                {isUpdating ? (
                  <CircularProgress size={24} />
                ) : (
                  "Update Reservation"
                )}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderEdit;
